import { TabMenu, TabMenuContent, TabMenuList, TabMenuTrigger } from '@ads/components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/shared/ui/organisms/PageHeader';

import { useTutorUsageFilters } from '../../../hooks/useTutorUsageFilters';
import { TokenUsagePanel } from '../../organisms/TokenUsagePanel';
import { UsageFilters } from '../../organisms/UsageFilters';
import { UsageOverviewPanel } from '../../organisms/UsageOverviewPanel';

/** Abas do dashboard de uso. */
type UsageTab = 'overview' | 'tokens';

const TABS: readonly UsageTab[] = ['overview', 'tokens'];

/**
 * Template do dashboard de uso do tutor, em duas abas sobre a mesma barra de filtros.
 *
 * "Visão geral" consome o relatório do dashboard (KPIs do período + evolução diária de
 * tokens e mensagens); "Consumo de tokens" consome o relatório de tokens agregado, que é o
 * único com composição por provedor/operação e histórico mensal.
 *
 * O filtro de período é compartilhado por construção, não por sincronização: as duas abas
 * leem o mesmo `tutorUsageFiltersAtom`, então trocar de aba preserva as datas. O recorte por
 * turma só aparece na primeira — o endpoint da segunda não o aceita.
 *
 * Só o painel ativo é montado, então a aba inativa não dispara consulta.
 *
 * A série de um aluno específico continua em `/tutor-ai/students/$personId`.
 */
export function TutorUsageTemplate() {
  const { t } = useTranslation('tutorAI');
  const { filters, setDateStart, setDateEnd, setClassStudent, reset } = useTutorUsageFilters();
  const [activeTab, setActiveTab] = useState<UsageTab>('overview');

  return (
    <div className="flex flex-col gap-xs">
      <PageHeader title={t('usage.title')} subtitle={t('usage.subtitle')} />

      <UsageFilters
        filters={filters}
        onDateStartChange={setDateStart}
        onDateEndChange={setDateEnd}
        onClassChange={setClassStudent}
        onClear={reset}
        showClassFilter={activeTab === 'overview'}
      />

      <TabMenu
        className="w-full"
        value={activeTab}
        onValueChange={value => setActiveTab(value as UsageTab)}
      >
        <TabMenuList className="w-full">
          {TABS.map(tab => (
            <TabMenuTrigger className="w-full" key={tab} value={tab}>
              {t(`usage.tabs.${tab}` as never)}
            </TabMenuTrigger>
          ))}
        </TabMenuList>
        <TabMenuContent value={activeTab}>
          <div className="pt-xs">
            {activeTab === 'overview' ? <UsageOverviewPanel /> : <TokenUsagePanel />}
          </div>
        </TabMenuContent>
      </TabMenu>
    </div>
  );
}
